<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { AdvancedCardV2, CardGrid, Select, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getVirtualComponents,
		getVirtualComponentsInterface,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { VIRTUAL_TEMPLATES } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_config';
	//Components
	import CreateVComponent from '../../Settings/CreateVComponent.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { VirtualComponentInterface } from 'src/types/VirtualComponents';
	import type {
		I18nCtx,
		VirtualDeviceTemplate,
	} from 'src/helpers/virtual_helpers/template_helpers/virtual_template_types';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

	export let parentDevice: Device;
	export let virtualTemplate: VirtualDeviceTemplate;

	type VirtualTemplateOption = {
		title: string;
		value: string;
		disabled: boolean;
		innerKey: boolean;
	};

	const selectedTemplate = VIRTUAL_TEMPLATES[virtualTemplate.type];
	const ctx: I18nCtx = { translate: (k, d) => $format(k, { default: d }) };
	const getComponentName = (item: VirtualComponentInterface) => {
		return item.config?.name ? `${item.config.name} (${item.key})` : item.key;
	};
	const getVirtualTemplateComponents = (
		role: string,
		virtualTemplate: VirtualDeviceTemplate,
		_virtualComponents: string[],
	): {
		options: VirtualTemplateOption[];
		selected: VirtualTemplateOption | null;
		label: string;
	} => {
		const component = selectedTemplate[role].type;
		const vcComponents = getVirtualComponentsInterface(parentDevice?.id, component) || [];
		const options = vcComponents.map((i) => ({
			title: getComponentName(i),
			value: i.key,
			disabled: Object.values(virtualTemplate).some((j) => j === i.key),
			innerKey: false,
		}));
		let selected = options.find((i) => i.value === virtualTemplate?.[role]) || null;
		if (
			virtualTemplate[role] &&
			vcComponents.find((i) => i.key === virtualTemplate[role]) === undefined
		) {
			const missingComp = {
				title:
					$format(`device_component_missing`, { default: 'Device component is missing' }) +
					' ' +
					virtualTemplate[role],
				value: null,
				disabled: true,
				innerKey: false,
			};
			selected = missingComp;
			options.push(missingComp);
		}
		options.push({
			title: $format('vc_template_create_component', {
				default: 'Create virtual component',
			}),
			value: 'create',
			disabled: false,
			innerKey: true,
		});
		if (selected) {
			options.push({
				title: $format('option_none', {
					default: 'None',
				}),
				value: 'none',
				disabled: false,
				innerKey: true,
			});
		}
		return {
			options,
			selected,
			label: $format('vc_template_select_component', {
				default: 'Select virtual component',
			}),
		};
	};
	const onVComponentChange = (_deviceStatus: DeviceStatusG2) => {
		const vcomps = getVirtualComponents(parentDevice.id, 'boolean|enum|text|button|number|group');
		if (virtualComponents.length !== vcomps.length) {
			virtualComponents = vcomps;
		}
	};

	let showVCModalCreate = false;
	let createComponentRole: string = null;
	let virtualComponents: string[] = [];

	$: statusStore = getDeviceStatus(parentDevice?.id);
	$: onVComponentChange($statusStore);
</script>

<Text styleType="body-small" class="mb-s align-self-end text-secondary">
	{$format('vc_template_components_count', {
		default: 'Groups and components: {count}/{max}',
		values: { count: virtualComponents.length, max: 10 },
	})}
</Text>
<CardGrid lg={290} sm={270} noPaddingBottom gridGap="s">
	{#each Object.keys(selectedTemplate) as role}
		{#if role !== 'type'}
			{@const vComponents = getVirtualTemplateComponents(role, virtualTemplate, virtualComponents)}
			<AdvancedCardV2
				isCondensed
				aria-label={role}
				title={capFirstChar(
					selectedTemplate[role]?.label ? selectedTemplate[role].label(ctx) : role,
				)}
				avatar={{ icon: selectedTemplate[role]?.icon }}
				description={{
					value: selectedTemplate[role]?.optional
						? $format('vc_template_label_optional', {
								default: 'Optional',
							})
						: $format('vc_template_label_required', {
								default: 'Required',
							}),
					icon: 'far fa-info-circle',
					type: selectedTemplate[role]?.optional ? 'muted' : 'secondary',
				}}
			>
				<Select
					slot="main"
					id="select-virtual-component"
					aria-label="Select virtual component"
					label={vComponents.label}
					value={vComponents.selected}
					options={vComponents.options}
					icon={null}
					on:change={(e) => {
						if (e.detail['innerKey'] === true) {
							delete virtualTemplate[role];
							if (e.detail['value'] === 'create') {
								createComponentRole = role;
								showVCModalCreate = true;
							}
						} else {
							virtualTemplate[role] = e.detail['value'];
						}
						virtualTemplate = virtualTemplate;
					}}
				/>
			</AdvancedCardV2>
		{/if}
	{/each}
</CardGrid>

{#if showVCModalCreate}
	<CreateVComponent
		device={parentDevice}
		filterType={selectedTemplate[createComponentRole]?.type}
		initSettings={{
			...(selectedTemplate[createComponentRole]?.create_config || {}),
			name: selectedTemplate[createComponentRole]?.label
				? selectedTemplate[createComponentRole].label(ctx)
				: createComponentRole,
		}}
		bind:showWizard={showVCModalCreate}
		on:save={(e) => {
			virtualTemplate[createComponentRole] = e.detail;
		}}
	/>
{/if}
